/**
 * ═══ DATES, WRITTEN THE WAY THE PEOPLE READING THEM WRITE THEM ═══
 *
 * ⚠ WHY THIS FILE EXISTS. Twenty-seven places called `new Date(x).toLocaleDateString()` with no
 * locale. That formats in the locale of the MACHINE, and a very large share of Indian school offices
 * run en-US Windows — so a date the product meant as 2 April rendered `4/2/2026` and was read by the
 * clerk as 4 February. Nothing errors. Nothing looks wrong. A fee due date, a trip's consent
 * deadline and an exam date are simply a month or two out for the person acting on them.
 *
 * ⚠ AND IT IS NOT FIXED BY SETTING THE BROWSER LOCALE. The product is sold to a school; the school
 * owns the laptops; nobody is going to reconfigure a front office. The only version of this that
 * works is the application deciding, which is what these helpers do.
 *
 * ⚠ THE FORMAT IS `dd MMM yyyy`, NOT `dd/mm/yyyy`. An all-numeric Indian date is unambiguous to an
 * Indian and ambiguous to everybody else, including a support engineer reading a screenshot. A
 * written month cannot be misread by anyone: "02 Apr 2026" means one thing in every country.
 */

const LOCALE = 'en-IN';

/** ⚠ Not `undefined` — the school's timezone, so a UTC timestamp near midnight is not a day out. */
const TZ = 'Asia/Kolkata';

/** A date on its own: "02 Apr 2026". */
export function formatDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(LOCALE, { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ });
}

/** A date and a time: "02 Apr 2026, 3:40 pm". */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString(LOCALE, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZone: TZ,
  });
}

/** Just the time: "3:40 pm". */
export function formatTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString(LOCALE, { hour: 'numeric', minute: '2-digit', timeZone: TZ });
}

/**
 * ⚠ AN UNPARSEABLE DATE RENDERS AS AN EM DASH, NOT "Invalid Date".
 *
 * A null due date, an empty string from an optional column, a malformed value from an import — all
 * of them used to produce the literal text "Invalid Date" on screen, which reads as a broken product
 * rather than a blank field. A dash reads as "not set", which is what it is.
 */
function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}
